import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, X, ArrowRight, NotebookPen } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const emptyEntry = () => ({ event: "", thought: "", emotion: "", reframe: "" });

export default function ThoughtRecordStep({ sessionNumber, title, subtitle, onDone }) {
  const [entries, setEntries] = useState([emptyEntry(), emptyEntry(), emptyEntry()]);
  const [saving, setSaving] = useState(false);

  const update = (i, k, v) => setEntries(entries.map((e, idx) => (idx === i ? { ...e, [k]: v } : e)));
  const add = () => setEntries([...entries, emptyEntry()]);
  const remove = (i) => setEntries(entries.filter((_, idx) => idx !== i));

  const save = async () => {
    const valid = entries.filter(e => e.event.trim() && e.thought.trim());
    if (valid.length < 1) {
      toast.error("Registra al menos un evento con su pensamiento.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/progress", { session_number: sessionNumber, step: "thought-record", completed: true, payload: { entries: valid } });
      toast.success("Diario guardado");
      onDone(valid);
    } catch {
      toast.error("No pudimos guardar tu diario.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {subtitle && <p className="text-[var(--ha-muted)] leading-relaxed max-w-2xl">{subtitle}</p>}
      <div className="mt-8 space-y-4">
        {entries.map((e, i) => (
          <Card key={i} className="rounded-2xl border-[var(--ha-border)] bg-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <NotebookPen className="w-4 h-4 text-[var(--ha-primary)]" strokeWidth={1.75} />
                  <p className="text-xs uppercase tracking-[0.14em] text-[var(--ha-muted)] font-bold">Entrada {i + 1}</p>
                </div>
                {entries.length > 1 && (
                  <button onClick={() => remove(i)} className="h-8 w-8 rounded-full grid place-items-center hover:bg-[var(--ha-bg)]" aria-label="Eliminar">
                    <X className="w-4 h-4 text-[var(--ha-muted)]" strokeWidth={1.75} />
                  </button>
                )}
              </div>
              <div className="grid gap-3">
                <Input data-testid={`tr-${i}-event`} placeholder="Evento (A)" value={e.event} onChange={(ev) => update(i, "event", ev.target.value)} className="rounded-xl border-[var(--ha-border)]" />
                <Textarea data-testid={`tr-${i}-thought`} placeholder="Pensamiento automático (B)" value={e.thought} onChange={(ev) => update(i, "thought", ev.target.value)} className="rounded-xl border-[var(--ha-border)]" />
                <Input data-testid={`tr-${i}-emotion`} placeholder="Emoción y su intensidad 0-100 (C)" value={e.emotion} onChange={(ev) => update(i, "emotion", ev.target.value)} className="rounded-xl border-[var(--ha-border)]" />
                <Textarea data-testid={`tr-${i}-reframe`} placeholder="Pensamiento alternativo más realista" value={e.reframe} onChange={(ev) => update(i, "reframe", ev.target.value)} className="rounded-xl border-[var(--ha-border)]" />
              </div>
            </CardContent>
          </Card>
        ))}
        <button onClick={add} data-testid="tr-add" className="inline-flex items-center gap-1.5 text-sm text-[var(--ha-primary)] hover:underline">
          <Plus className="w-4 h-4" strokeWidth={1.75} /> Añadir otra entrada
        </button>
      </div>
      <div className="mt-8 flex justify-end">
        <Button data-testid="tr-continue-btn" onClick={save} disabled={saving} className="rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-11 px-6">
          Guardar y continuar <ArrowRight className="w-4 h-4 ml-1.5" strokeWidth={1.75} />
        </Button>
      </div>
    </div>
  );
}
